import { Badge } from '@/components/ui/badge'
import { AlertCircle } from 'lucide-react'

/**
 * Help content specific to blog posts
 */
export function BlogPostHelp() {
    return (
        <>
            <h3 className="font-medium text-lg mb-2">Publishing Blog Posts</h3>
            <div className="space-y-5">
                <div>
                    <h4 className="font-medium text-base mb-2">Publishing Options</h4>
                    <div className="bg-secondary/30 p-4 rounded-md border border-border mb-4">
                        <ul className="grid gap-3">
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 1</Badge>
                                <div>
                                    <span className="font-medium">HTML Export:</span> Copy the HTML and host it on your own server or CMS
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 2</Badge>
                                <div>
                                    <span className="font-medium">Direct Link:</span> Share the hosted version directly from Rainfactory's servers
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 3</Badge>
                                <div>
                                    <span className="font-medium">Embed:</span> Embed the blog post on your existing website using an iframe
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <div>
                    <h4 className="font-medium text-base mb-2">Step-by-step Publishing Guide</h4>
                    <ol className="list-decimal pl-5 space-y-3 mb-4">
                        <li>
                            <span className="font-medium">Finalize your content:</span> Ensure all text, images, and formatting are ready
                            <div className="pl-5 mt-1 text-sm text-muted-foreground">
                                <p>Use the <Badge variant="outline" className="mx-1 font-normal">Improve Content</Badge> feature to optimize your blog post</p>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Set status to Published:</span> Open the content settings and change status to 
                            <Badge className="ml-1 mr-1 font-normal">Published</Badge>
                            <div className="pl-5 mt-1 text-sm">
                                <p className="text-muted-foreground">This marks the content as ready for public viewing and enables all sharing features</p>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Choose your publishing method:</span>
                            <div className="pl-5 mt-1 space-y-2">
                                <div>
                                    <p className="font-medium text-sm">For HTML/Custom hosting:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Select <span className="font-semibold">Copy HTML</span></li>
                                        <li>Paste the HTML into your CMS or hosting platform</li>
                                    </ol>
                                </div>
                                <div>
                                    <p className="font-medium text-sm">For Direct Link:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Click <span className="font-semibold">Open as Webpage</span></li>
                                        <li>Copy the URL from your browser address bar</li>
                                    </ol>
                                </div>
                                <div>
                                    <p className="font-medium text-sm">For Embedding:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Click <span className="font-semibold">Copy Preview URL</span></li>
                                        <li>Use this URL in an iframe on your site</li>
                                    </ol>
                                </div>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">SEO Optimization:</span> Ensure your blog post has appropriate meta titles, descriptions, and keywords
                            <div className="pl-5 mt-1 text-sm text-muted-foreground">
                                <p>This will help maximize search engine visibility for your content</p>
                            </div>
                        </li>
                    </ol>
                </div>

                <div>
                    <h4 className="font-medium text-base mb-2">Making Updates After Publishing</h4>
                    <div className="bg-secondary/30 p-4 rounded-md border border-border mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                            <li>Return to this page and make your desired changes</li>
                            <li>Use <Badge variant="outline" className="mx-1 font-normal">Improve Content</Badge> or edit directly</li>
                            <li>A new version will be created automatically</li>
                            <li>For embedded or direct links, the content updates automatically</li>
                        </ol>
                    </div>
                </div>
                    
                <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-md border border-amber-200 dark:border-amber-800 mb-4">
                    <h4 className="font-medium text-amber-800 dark:text-amber-400 flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4" />
                        Blog Post Best Practices
                    </h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-amber-700 dark:text-amber-300">
                        <li>Test all links and media to ensure they work properly</li>
                        <li>Verify the post is mobile-responsive using the preview mode</li>
                        <li>Check that images are properly optimized for web</li>
                        <li>Confirm that all formatting displays correctly</li>
                        <li>Review the post for typos and grammatical errors</li>
                    </ul>
                </div>
            </div>
        </>
    )
}